/**
 * Position-aware soccer formation placement.
 *
 * Depth on pitch (goal at defensive end):
 * - Goalkeeper: on the goal line
 * - Defender: defensive third (protect own goal)
 * - Midfielder: middle third (link defense and attack)
 * - Winger: wide channels between midfield and attack
 * - Forward: attacking third (nearest opposition goal)
 * - Flexible: fills open lines; one may be promoted to GK if none exists
 */

const POSITION_ORDER = ['GOALKEEPER', 'DEFENDER', 'MIDFIELDER', 'WINGER', 'FORWARD', 'FLEXIBLE'];

const HORIZONTAL_DEPTH = {
  GOALKEEPER: 10,
  DEFENDER: 22,
  MIDFIELDER: 48,
  WINGER: 62,
  FORWARD: 78,
  FLEXIBLE: 48,
};

const VERTICAL_DEPTH = {
  GOALKEEPER: 90,
  DEFENDER: 74,
  MIDFIELDER: 52,
  WINGER: 40,
  FORWARD: 26,
  FLEXIBLE: 52,
};

const FORMATION_LINE_COUNTS = {
  '4-4-2': { GOALKEEPER: 1, DEFENDER: 4, MIDFIELDER: 4, FORWARD: 2, WINGER: 0 },
  '4-3-3': { GOALKEEPER: 1, DEFENDER: 4, MIDFIELDER: 3, FORWARD: 3, WINGER: 0 },
  '3-5-2': { GOALKEEPER: 1, DEFENDER: 3, MIDFIELDER: 5, FORWARD: 2, WINGER: 0 },
  '4-2-3-1': { GOALKEEPER: 1, DEFENDER: 4, MIDFIELDER: 2, WINGER: 2, FORWARD: 1 },
  '3-4-3': { GOALKEEPER: 1, DEFENDER: 3, MIDFIELDER: 4, FORWARD: 3, WINGER: 0 },
  '1-2-1': { GOALKEEPER: 1, DEFENDER: 2, MIDFIELDER: 1, FORWARD: 1, WINGER: 0 },
  '1-2-2': { GOALKEEPER: 1, DEFENDER: 2, MIDFIELDER: 2, FORWARD: 1, WINGER: 0 },
  '1-3-1': { GOALKEEPER: 1, DEFENDER: 3, MIDFIELDER: 1, FORWARD: 1, WINGER: 0 },
  '1-3-2': { GOALKEEPER: 1, DEFENDER: 3, MIDFIELDER: 2, FORWARD: 1, WINGER: 0 },
  '1-2-3': { GOALKEEPER: 1, DEFENDER: 2, MIDFIELDER: 2, FORWARD: 3, WINGER: 0 },
  '1-3-3': { GOALKEEPER: 1, DEFENDER: 3, MIDFIELDER: 3, FORWARD: 1, WINGER: 0 },
  '1-2-4': { GOALKEEPER: 1, DEFENDER: 2, MIDFIELDER: 2, FORWARD: 4, WINGER: 0 },
  '1-3-4': { GOALKEEPER: 1, DEFENDER: 3, MIDFIELDER: 4, FORWARD: 2, WINGER: 0 },
  '1-4-3': { GOALKEEPER: 1, DEFENDER: 4, MIDFIELDER: 2, FORWARD: 2, WINGER: 0 },
  '1-4-4': { GOALKEEPER: 1, DEFENDER: 4, MIDFIELDER: 3, FORWARD: 2, WINGER: 0 },
  '1-3-5': { GOALKEEPER: 1, DEFENDER: 3, MIDFIELDER: 4, FORWARD: 2, WINGER: 0 },
};

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

const hashSeed = (parts) =>
  parts.filter(Boolean).join('|').split('').reduce((h, c) => ((h << 5) - h) + c.charCodeAt(0), 0);

export const getEffectivePosition = (player) => player._effectivePosition || player.position || 'FLEXIBLE';

/** Promote a flexible player to goalkeeper when the team has no GK. */
export const resolveEffectivePositions = (players, seed = '') => {
  const hasGoalkeeper = players.some((p) => (p.position || 'FLEXIBLE') === 'GOALKEEPER');
  if (hasGoalkeeper) {
    return players.map((p) => ({
      ...p,
      _effectivePosition: p.position || 'FLEXIBLE',
      _promotedGoalkeeper: false,
    }));
  }

  const flexiblePlayers = players.filter((p) => (p.position || 'FLEXIBLE') === 'FLEXIBLE');
  if (flexiblePlayers.length === 0) {
    return players.map((p) => ({
      ...p,
      _effectivePosition: p.position || 'FLEXIBLE',
      _promotedGoalkeeper: false,
    }));
  }

  const sorted = [...flexiblePlayers].sort((a, b) =>
    (a.id || a.name || '').localeCompare(b.id || b.name || '', undefined, { sensitivity: 'base' })
  );
  const pick = sorted[Math.abs(hashSeed([seed, ...sorted.map((p) => p.id || p.name)])) % sorted.length];

  return players.map((p) => ({
    ...p,
    _effectivePosition: p.id === pick.id ? 'GOALKEEPER' : (p.position || 'FLEXIBLE'),
    _promotedGoalkeeper: p.id === pick.id,
  }));
};

const groupPlayersByEffectivePosition = (players) => {
  const groups = {};
  POSITION_ORDER.forEach((key) => {
    groups[key] = [];
  });
  players.forEach((player) => {
    const key = getEffectivePosition(player);
    if (groups[key]) {
      groups[key].push(player);
    } else {
      groups.FLEXIBLE.push(player);
    }
  });
  Object.keys(groups).forEach((key) => {
    groups[key].sort((a, b) => (b.skillLevel || 0) - (a.skillLevel || 0));
  });
  return groups;
};

const spreadAxis = (count, min, max) => {
  if (count <= 0) return [];
  if (count === 1) return [(min + max) / 2];
  return Array.from({ length: count }, (_, i) => min + (i * (max - min)) / (count - 1));
};

const VERTICAL_SPREAD_MIN = 15;
const VERTICAL_SPREAD_MAX = 85;

/**
 * Build vertical formation slot coordinates from FORMATION_LINE_COUNTS.
 * Used for small-sided formations (e.g. 1-4-4) that lack static templates.
 */
export const buildVerticalFormationTemplate = (formationKey) => {
  const lineCounts = FORMATION_LINE_COUNTS[formationKey];
  if (!lineCounts) return null;

  const template = {};
  POSITION_ORDER.forEach((position) => {
    const count = lineCounts[position] || 0;
    if (count <= 0) {
      template[position] = [];
      return;
    }
    const depth = VERTICAL_DEPTH[position];
    const widthCoords = position === 'WINGER'
      ? wideWingerAxis(count)
      : spreadAxis(count, VERTICAL_SPREAD_MIN, VERTICAL_SPREAD_MAX);
    template[position] = widthCoords.map((x) => ({ x, y: depth }));
  });
  return template;
};

const wideWingerAxis = (count) => {
  const slots = [];
  for (let i = 0; i < count; i += 1) {
    slots.push(i % 2 === 0 ? 14 : 86);
  }
  return slots;
};

const lineSlotCount = (groups, lineKey, formation) => {
  const actual = groups[lineKey]?.length || 0;
  const template = formation && FORMATION_LINE_COUNTS[formation]
    ? FORMATION_LINE_COUNTS[formation][lineKey] || 0
    : 0;
  if (lineKey === 'GOALKEEPER') {
    return actual;
  }
  return Math.max(actual, template);
};

const takePlayersForLine = (groups, lineKey, count) => {
  const primary = [...(groups[lineKey] || [])];
  const taken = primary.splice(0, count);
  const remaining = count - taken.length;
  if (remaining <= 0) {
    groups[lineKey] = primary;
    return taken;
  }

  const flex = [...(groups.FLEXIBLE || [])];
  const fromFlex = flex.splice(0, remaining);
  groups[lineKey] = primary;
  groups.FLEXIBLE = flex;
  return [...taken, ...fromFlex];
};

const coordForPlayer = (depth, width, vertical) =>
  vertical
    ? { x: clamp(width, 10, 90), y: clamp(depth, 12, 92) }
    : { x: clamp(depth, 8, 92), y: clamp(width, 10, 90) };

const placeLine = (linePlayers, depth, vertical, { wide = false } = {}) => {
  const widthCoords = wide ? wideWingerAxis(linePlayers.length) : spreadAxis(linePlayers.length, 14, 86);
  return linePlayers.map((player, index) => ({
    player,
    ...coordForPlayer(depth, widthCoords[index] ?? 50, vertical),
  }));
};

const placeFlexiblePool = (players, vertical, depthMap) => {
  if (players.length === 0) return [];

  const bands = [
    depthMap.MIDFIELDER,
    depthMap.DEFENDER,
    depthMap.FORWARD,
    depthMap.WINGER,
    depthMap.MIDFIELDER + (vertical ? -6 : 0),
  ];

  const positions = [];
  players.forEach((player, index) => {
    const depth = bands[index % bands.length];
    const width = spreadAxis(players.length, 18, 82)[index] ?? 50;
    positions.push({
      player,
      ...coordForPlayer(depth, width, vertical),
    });
  });
  return positions;
};

export const buildPositionBasedLayout = (players, { vertical = false, formation = null, seed = '' } = {}) => {
  const prepared = resolveEffectivePositions(players, seed);
  const groups = groupPlayersByEffectivePosition(prepared);
  const depthMap = vertical ? VERTICAL_DEPTH : HORIZONTAL_DEPTH;
  const positions = [];

  const gkPlayers = takePlayersForLine(groups, 'GOALKEEPER', Math.max(groups.GOALKEEPER.length, 0));
  if (gkPlayers.length > 0) {
    positions.push(...placeLine(gkPlayers, depthMap.GOALKEEPER, vertical));
  }

  const lineKeys = ['DEFENDER', 'MIDFIELDER', 'WINGER', 'FORWARD'];
  lineKeys.forEach((lineKey) => {
    const slotCount = lineSlotCount(groups, lineKey, formation);
    if (slotCount <= 0) return;
    const linePlayers = takePlayersForLine(groups, lineKey, slotCount);
    if (linePlayers.length === 0) return;
    positions.push(
      ...placeLine(linePlayers, depthMap[lineKey], vertical, { wide: lineKey === 'WINGER' })
    );
  });

  if (groups.FLEXIBLE.length > 0) {
    positions.push(...placeFlexiblePool(groups.FLEXIBLE, vertical, depthMap));
  }

  return positions;
};

export const slotMatchScore = (player, slotPosition) => {
  const playerPosition = getEffectivePosition(player);
  if (playerPosition === slotPosition) return 0;
  if (playerPosition === 'FLEXIBLE') return 2;
  if (slotPosition === 'WINGER' && playerPosition === 'MIDFIELDER') return 1;
  if (slotPosition === 'MIDFIELDER' && (playerPosition === 'WINGER' || playerPosition === 'FORWARD')) return 1;
  if (slotPosition === 'FORWARD' && playerPosition === 'WINGER') return 1;
  if (slotPosition === 'DEFENDER' && playerPosition === 'MIDFIELDER') return 3;
  if (slotPosition === 'GOALKEEPER' && playerPosition !== 'GOALKEEPER') return 6;
  return 4;
};

export const assignPlayersToFormationSlots = (players, formationSlots, { seed = '' } = {}) => {
  const prepared = resolveEffectivePositions(players, seed);
  const unassigned = [...prepared].sort((a, b) => (b.skillLevel || 0) - (a.skillLevel || 0));
  const positions = [];

  formationSlots.forEach((slot) => {
    if (unassigned.length === 0) return;

    const ranked = [...unassigned].sort((a, b) => {
      const scoreDiff = slotMatchScore(a, slot.slotPosition) - slotMatchScore(b, slot.slotPosition);
      if (scoreDiff !== 0) return scoreDiff;
      return (b.skillLevel || 0) - (a.skillLevel || 0);
    });

    const player = ranked[0];
    positions.push({ player, x: slot.coord.x, y: slot.coord.y });
    const removeIndex = unassigned.findIndex((p) => p.id === player.id);
    if (removeIndex >= 0) unassigned.splice(removeIndex, 1);
  });

  return { positions, unassigned };
};
